import {
  type ComponentPropsWithoutRef,
  type ComponentPropsWithRef,
  type ElementType,
  type JSX,
  useMemo,
} from 'react';

import { pick } from 'lodash-es';

/* eslint-disable-next-line import/order
  -- This is necessary to avoid conflicting rules with import/order and import/newline-after-import,
     due to the comment that exists to disable the no-restricted-imports rule. */
import { type BaseProps, type OverridableTypeMap } from '@mui/material/OverridableComponent';

/* eslint-disable-next-line no-restricted-imports */
import RootTypography, { type TypographyTypeMap } from '@mui/material/Typography';

import { mergeSx } from '../../util/mui/sx';
import { IfNodeRenders } from '../util/IfNodeRenders';

import * as types from './types';
import * as util from './util';

/**
 * Represents the root MUI props for the {@link Typography} component, accounting for the
 * polymorphic nature of the props and component that can be controlled via the 'component' prop
 * that is passed to the component.
 *
 * This type must be generated by distributing over the union {@link T}, which is a type map that
 * already distributes over the union {@link E}.  This distribution is necessary in order for the
 * props to be properly detected and typed in the presence of different polymorphic values that
 * can be provided for the 'component' prop, represented by {@link E}.
 *
 * This type is analogous to Material UI's {@link OverrideProps} type.
 *
 * @see https://mui.com/material-ui/guides/composition/#generic for more information related to the
 * extension of generically typed Material UI components.
 */
type RootTypographyProps<
  E extends ElementType,
  T extends OverridableTypeMap,
  /* Forces the intersection type to be distributed over the union type T. */
> = T extends OverridableTypeMap
  ? BaseProps<T> & Omit<ComponentPropsWithoutRef<E>, keyof BaseProps<T>>
  : never;

export type TypographyDefaultComponent = TypographyTypeMap['defaultComponent'];

export type TypographyPartialProps = {
  readonly inherit?: true | types.InheritableTypographyProperty[];
  readonly lineClamp?: types.LineClamp;
  readonly truncate?: boolean;
  readonly wordBreak?: 'break-all' | 'break-word';
};

/**
 * Represents the overall props for the internalized {@link Typography} component, which are a
 * combination of internally defined props, {@link TypographyPartialProps}, and props that are
 * extended directly from MUI, {@link RootTypographyProps}, accounting for the polymorphic nature
 * of the {@link Typography} component which can be controlled via the 'component' prop:
 *
 * @example
 * <Typography component={Link} href="/home">Home</Typography>
 */
export type TypographyProps<
  E extends ElementType = TypographyDefaultComponent,
  P = TypographyPartialProps,
> = P & RootTypographyProps<E, TypographyTypeMap<{ component?: E }, E>>;

export const Typography = <E extends ElementType>({
  children,
  color,
  fontFamily,
  fontSize,
  fontWeight,
  inherit,
  letterSpacing,
  lineClamp,
  lineHeight,
  ref,
  textDecoration,
  truncate,
  wordBreak = 'break-word',
  ...props
}: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & TypographyProps<E>): JSX.Element => {
  /**
   * Defines the SX that will be attributed to the {@link Typography} component that causes the
   * element to inherit the font-specific properties from its parent in the case that the
   * 'inherit' prop is 'true' or an array of font-specific properties to inherit.
   *
   * The "inherit" CSS property value is only applied to properties that are not explicitly
   * defined on the props that the {@link Typography} component receives.  For example,
   * if the element is rendered as
   *
   * >>> <Typography inherit fontSize="20px" />
   *
   * Then the 'fontSize' will not be set to 'inherit' in the SX, but the provided '20px' value
   * will be favored.
   */
  const inheritedSx = useMemo(() => {
    const inheritableProps = {
      color,
      fontFamily,
      fontSize,
      fontWeight,
      letterSpacing,
      lineHeight,
      textDecoration,
    };
    if (inherit) {
      return types.inheritedTypographySx(
        types.InheritableTypographyProperties.reduce(
          (acc: types.InheritableTypographyProperty[], prop) => {
            if (
              inheritableProps[prop] === undefined &&
              (inherit === true || inherit.includes(prop))
            ) {
              return [...acc, prop];
            }
            return acc;
          },
          [],
        ),
      );
    }
    return {};
  }, [color, fontSize, fontWeight, fontFamily, letterSpacing, lineHeight, textDecoration, inherit]);

  const clampedStyle = util.parseTypographyTruncatedStyle({
    ...pick(props, 'display', 'overflow', 'textOverflow', 'whiteSpace'),
    lineClamp,
    truncate,
  });

  return (
    <IfNodeRenders node={children}>
      <RootTypography
        {...props}
        color={color}
        display={clampedStyle.display}
        fontSize={fontSize}
        fontWeight={fontWeight}
        letterSpacing={letterSpacing}
        lineHeight={lineHeight}
        overflow={clampedStyle.overflow}
        ref={ref}
        sx={mergeSx({ wordBreak }, inheritedSx, props.sx, clampedStyle)}
        textDecoration={textDecoration}
        textOverflow={clampedStyle.textOverflow}
        whiteSpace={clampedStyle.whiteSpace}
      >
        {children}
      </RootTypography>
    </IfNodeRenders>
  );
};
